import { supabase } from '../../../lib/supabaseClient';

const formatDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const addDays = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return formatDate(dt);
};

// Unblock a single calendar day within a (possibly multi-day) blocked_time row.
// - Single-day row  → delete it.
// - Clicked day is the first day of the range → shrink start to the next day.
// - Clicked day is the last day of the range  → shrink end to the previous day.
// - Clicked day is in the middle               → split into two rows.
// Returns true if the caller should refresh events from the server.
export async function unblockDay(blockId: string | number, dayDate: Date): Promise<boolean> {
  const { data: row, error } = await supabase
    .from('staff_availability')
    .select('*')
    .eq('id', blockId)
    .single();

  if (error || !row) {
    console.error('unblockDay: failed to load block', error);
    return false;
  }

  // Recurring blocks don't have a date range; fall back to deleting the whole row.
  if (row.is_recurring || !row.specific_date) {
    await supabase.from('staff_availability').delete().eq('id', blockId);
    return true;
  }

  const startDate: string = row.specific_date;
  const endDate: string = row.end_date ?? row.specific_date;
  const clicked = formatDate(dayDate);

  // Clicked day is outside the block (shouldn't happen from the UI, but be safe).
  if (clicked < startDate || clicked > endDate) {
    await supabase.from('staff_availability').delete().eq('id', blockId);
    return true;
  }

  // Single-day block: delete.
  if (startDate === endDate) {
    await supabase.from('staff_availability').delete().eq('id', blockId);
    return true;
  }

  // First day of a multi-day block: shrink start forward by one day.
  if (clicked === startDate) {
    await supabase
      .from('staff_availability')
      .update({
        specific_date: addDays(startDate, 1),
        start_time: '00:00:00',
      })
      .eq('id', blockId);
    return true;
  }

  // Last day of a multi-day block: shrink end back by one day.
  if (clicked === endDate) {
    await supabase
      .from('staff_availability')
      .update({
        end_date: addDays(endDate, -1),
        end_time: '23:59:59',
      })
      .eq('id', blockId);
    return true;
  }

  // Middle day: truncate this row at the previous day, insert a new row for
  // the tail starting the next day. Preserves staff, reason, and end time.
  await supabase
    .from('staff_availability')
    .update({
      end_date: addDays(clicked, -1),
      end_time: '23:59:59',
    })
    .eq('id', blockId);

  await supabase.from('staff_availability').insert({
    staff_id: row.staff_id,
    specific_date: addDays(clicked, 1),
    end_date: endDate,
    start_time: '00:00:00',
    end_time: row.end_time,
    is_recurring: false,
    notes: row.notes,
    day_of_week: row.day_of_week,
  });

  return true;
}

// Human-friendly confirm text. The caller uses window.confirm(promptForUnblock(...)).
export function promptForUnblock(block: {
  start: string | Date;
  end: string | Date;
}, dayDate: Date): string {
  const start = typeof block.start === 'string' ? new Date(block.start) : block.start;
  const end = typeof block.end === 'string' ? new Date(block.end) : block.end;
  const startDate = formatDate(start);
  const endDate = formatDate(end);
  const clicked = formatDate(dayDate);
  const human = dayDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  if (startDate === endDate) {
    return 'Delete this blocked time?';
  }
  if (clicked === startDate || clicked === endDate) {
    return `Unblock ${human}? The rest of the blocked range will remain.`;
  }
  return `Unblock ${human}? The block will be split around this day; the days before and after stay blocked.`;
}
